import { useEffect, useState } from 'react';
import { api, type Me } from '../lib/api';

interface AuditRow {
  event_id: string;
  tenant_id: string;
  user_id: string | null;
  category: string;
  action: string;
  correlation_id: string;
  this_hash: string;
  prev_event_hash: string;
  timestamp: number;
  payload: Record<string, unknown>;
}

function shortHash(h: string) { return h.slice(0, 8) + '…'; }

export default function Audit({ me }: { me: Me }) {
  const [events, setEvents] = useState<AuditRow[] | null>(null);
  const [verify, setVerify] = useState<{ checked: number; breaks: number; chain_head: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('');

  async function load() {
    try {
      setErr(null);
      const r = await api.auditRecent({ limit: 50, category: categoryFilter || undefined });
      setEvents(r.events);
    } catch (e: any) { setErr(e.message); }
  }
  async function runVerify() {
    setBusy(true);
    try { const r = await api.auditVerify(500); setVerify(r); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  useEffect(() => { load(); const t = setInterval(load, 30_000); return () => clearInterval(t); }, [categoryFilter]);

  const isSuperAdmin = me.role === 'super_admin';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Audit log</h1>
          <p className="text-gray-500 text-sm mt-1">
            Hash-chained, tamper-evident events. Written by <span className="font-mono">audit-write-svc</span> (the only writer to the chain).
          </p>
        </div>
        {isSuperAdmin && (
          <button onClick={runVerify} disabled={busy} className="btn-primary text-sm">
            {busy ? 'Verifying…' : 'Verify chain'}
          </button>
        )}
      </div>

      {!isSuperAdmin && (
        <div className="card p-4 text-sm text-gray-500">
          Showing audit events for your tenant. Chain-wide integrity verification is super-admin only.
        </div>
      )}

      {verify && (
        <div className={`card p-4 text-sm ${verify.breaks === 0 ? 'border-tnh-ok' : 'border-tnh-err'}`}>
          <div className="font-medium">
            {verify.breaks === 0
              ? `Chain intact — ${verify.checked} events verified, 0 breaks`
              : `Chain BROKEN — ${verify.breaks} of ${verify.checked} events failed verification`}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            head: <span className="font-mono">{verify.chain_head}</span>
          </div>
        </div>
      )}

      {err && <div className="card p-3 text-sm text-tnh-err">{err}</div>}

      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">Filter by category:</span>
        {['', 'auth', 'config', 'support', 'rule', 'gateway_denial', 'spoke', 'vault'].map((c) => (
          <button key={c || 'all'} onClick={() => setCategoryFilter(c)}
                  className={`px-3 py-1 rounded-md text-xs ${
                    categoryFilter === c ? 'bg-tnh-accent text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                  }`}>
            {c || 'all'}
          </button>
        ))}
      </div>

      {events === null && <div className="card p-8 text-center text-gray-500">Loading…</div>}
      {events && events.length === 0 && (
        <div className="card p-8 text-center text-gray-500">No audit events yet for this filter.</div>
      )}
      <div className="card divide-y divide-tnh-border dark:divide-tnh-border-dark text-sm">
        {events?.map((e) => (
          <div key={e.event_id} className="px-4 py-3">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="pill bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">{e.category}</span>
              <span className="font-mono text-sm">{e.action}</span>
              <span className="text-xs text-gray-500">{new Date(e.timestamp).toLocaleString()}</span>
              <span className="text-xs text-gray-400 ml-auto font-mono">{shortHash(e.this_hash)}</span>
            </div>
            <div className="mt-1 text-xs text-gray-500">
              tenant: <span className="font-mono">{e.tenant_id}</span> · user: <span className="font-mono">{e.user_id ?? 'system'}</span>
              {' '}· prev: <span className="font-mono">{shortHash(e.prev_event_hash)}</span>
            </div>
            {Object.keys(e.payload ?? {}).length > 0 && (
              <details className="mt-1 text-xs">
                <summary className="cursor-pointer text-gray-500">Payload</summary>
                <pre className="mt-1 p-2 bg-gray-50 dark:bg-gray-900 rounded text-[11px] overflow-x-auto font-mono">{JSON.stringify(e.payload, null, 2)}</pre>
              </details>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
